import {
  LEGACY_COPY_REVIEW_STEPS,
  PLAYBOOK_INSIGHT_KIND,
  PLAYBOOK_INSIGHT_RUN_STATUS,
  PLAYBOOK_INSIGHT_SOURCE,
  PLAYBOOK_INSIGHT_STATUS,
  PLAYBOOK_INSIGHT_STEP,
  PLAYBOOK_INSIGHT_STEPS_BY_SOURCE,
  PLAYBOOK_SOURCE_BLOCKED_REASON,
  PLAYBOOK_SOURCE_STATE,
  type PlaybookInsightRunStatus,
  type PlaybookInsightSource,
  type PlaybookInsightStep,
  type PlaybookSourceBlockedReason,
  type PlaybookSourceState,
} from './const';
import type { Playbook, PlaybookInsight, PlaybookInsightRun } from './types';

// `cooperPlaybookInsights` returns a playbook's pending and approved insights together, so each
// screen keeps only the ones it is about: the review wizard proposes what is still pending, the
// playbook card shows what was approved.
export const isPlaybookInsightPending = (insight: PlaybookInsight): boolean =>
  insight.status === PLAYBOOK_INSIGHT_STATUS.pending;

export const isPlaybookInsightApproved = (insight: PlaybookInsight): boolean =>
  insight.status === PLAYBOOK_INSIGHT_STATUS.approved;

export const isDirectionalInsight = (insight: PlaybookInsight): boolean =>
  insight.insightType === PLAYBOOK_INSIGHT_KIND.directional;

export const isPatternInsight = (insight: PlaybookInsight): boolean =>
  insight.insightType === PLAYBOOK_INSIGHT_KIND.pattern;

// Uploaded A/B tests measure real ads, as an integration run does, so the user sees their insights
// as integration data.
export const isIntegrationDataSource = (source: PlaybookInsightSource): boolean =>
  source === PLAYBOOK_INSIGHT_SOURCE.integration || source === PLAYBOOK_INSIGHT_SOURCE.manualData;

export const isIntegrationInsight = (insight: PlaybookInsight): boolean =>
  isIntegrationDataSource(insight.source);

// The account an integration run reads is chosen when the run starts. It can differ from the account
// the playbook was generated from, which stays the playbook's own. A run that recorded no account
// read the playbook's. Until the run exists, no account is known.
export const getPlaybookInsightRunAdAccountRef = (
  run: Pick<PlaybookInsightRun, 'adAccountRef'> | null,
  playbook: Pick<Playbook, 'adAccountRef'> | null
): string | null | undefined => (run ? (run.adAccountRef ?? playbook?.adAccountRef) : undefined);

// A directional insight measured an effect, but the number does not always survive the crossing
// from the source that produced it — so the kind says which list an insight belongs in, and this
// says whether there is a lift to put on it.
export const hasInsightLift = (insight: PlaybookInsight): boolean => insight.liftPct != null;

export const getPlaybookInsightSteps = (
  source: PlaybookInsightSource,
  status?: PlaybookInsightRunStatus
): PlaybookInsightStep[] =>
  status === PLAYBOOK_INSIGHT_RUN_STATUS.copyReady
    ? LEGACY_COPY_REVIEW_STEPS
    : PLAYBOOK_INSIGHT_STEPS_BY_SOURCE[source];

const RUN_STATUS_TO_STEP: Record<PlaybookInsightRunStatus, PlaybookInsightStep> = {
  [PLAYBOOK_INSIGHT_RUN_STATUS.generatingCopy]: PLAYBOOK_INSIGHT_STEP.copyReview,
  [PLAYBOOK_INSIGHT_RUN_STATUS.copyReady]: PLAYBOOK_INSIGHT_STEP.copyReview,
  [PLAYBOOK_INSIGHT_RUN_STATUS.analyzing]: PLAYBOOK_INSIGHT_STEP.insightsReview,
  [PLAYBOOK_INSIGHT_RUN_STATUS.insightsReady]: PLAYBOOK_INSIGHT_STEP.insightsReview,
  [PLAYBOOK_INSIGHT_RUN_STATUS.completed]: PLAYBOOK_INSIGHT_STEP.insightsReview,
  // The wizard shows the failure itself in place of the step; this only gives the stepper a position.
  [PLAYBOOK_INSIGHT_RUN_STATUS.failed]: PLAYBOOK_INSIGHT_STEP.copyReview,
  [PLAYBOOK_INSIGHT_RUN_STATUS.rejected]: PLAYBOOK_INSIGHT_STEP.copyReview,
};

// Clamped to the steps the source actually has: a status can only map onto a step the run can
// reach, and an integration run never has a Copy review to fall back to.
export const getPlaybookInsightStepFromStatus = (
  status: PlaybookInsightRunStatus,
  source: PlaybookInsightSource
): PlaybookInsightStep => {
  const steps = getPlaybookInsightSteps(source, status);
  const step = RUN_STATUS_TO_STEP[status];

  return steps.includes(step) ? step : steps[0];
};

const PROCESSING_STATUSES: PlaybookInsightRunStatus[] = [
  PLAYBOOK_INSIGHT_RUN_STATUS.generatingCopy,
  PLAYBOOK_INSIGHT_RUN_STATUS.analyzing,
];

export const isPlaybookInsightRunProcessing = (status?: PlaybookInsightRunStatus): boolean => {
  if (!status) {
    return false;
  }
  return PROCESSING_STATUSES.includes(status);
};

export const isPlaybookInsightRunFailed = (status?: PlaybookInsightRunStatus): boolean =>
  status === PLAYBOOK_INSIGHT_RUN_STATUS.failed;

// Waiting at a human gate: the pipeline paused with results (copy or insights) for the user to review.
const READY_STATUSES: PlaybookInsightRunStatus[] = [
  PLAYBOOK_INSIGHT_RUN_STATUS.copyReady,
  PLAYBOOK_INSIGHT_RUN_STATUS.insightsReady,
];

export const isPlaybookInsightRunReadyForReview = (status?: PlaybookInsightRunStatus): boolean => {
  if (!status) {
    return false;
  }
  return READY_STATUSES.includes(status);
};

// A run the user can still resume from the card — mid-pipeline or waiting at a human gate, but not
// yet finished, discarded, or failed.
export const isPlaybookInsightRunPending = (status?: PlaybookInsightRunStatus): boolean =>
  isPlaybookInsightRunProcessing(status) || isPlaybookInsightRunReadyForReview(status);

export const getPlaybookInsightRuns = (playbook: Playbook): PlaybookInsightRun[] =>
  playbook.insightRuns ?? [];

export const getPlaybookInsightRun = (
  playbook: Playbook,
  source: PlaybookInsightSource
): PlaybookInsightRun | null =>
  getPlaybookInsightRuns(playbook).find((run) => run.source === source) ?? null;

// The one run the playbook is currently busy with. The backend admits a single run in flight across
// every source, so the card can speak about "the run" without naming a source.
export const getActivePlaybookInsightRun = (playbook: Playbook): PlaybookInsightRun | null =>
  getPlaybookInsightRuns(playbook).find((run) => isPlaybookInsightRunPending(run.status)) ?? null;

// How one source presents on the card, distinguishing "still working" from "ready for you to
// review" — the two must look different so the user knows when results are waiting.
export const getPlaybookSourceState = (
  playbook: Playbook,
  source: PlaybookInsightSource
): PlaybookSourceState => {
  const status = getPlaybookInsightRun(playbook, source)?.status;

  if (isPlaybookInsightRunProcessing(status)) {
    return PLAYBOOK_SOURCE_STATE.processing;
  }
  if (isPlaybookInsightRunReadyForReview(status)) {
    return PLAYBOOK_SOURCE_STATE.ready;
  }
  if (status === PLAYBOOK_INSIGHT_RUN_STATUS.completed) {
    return PLAYBOOK_SOURCE_STATE.connected;
  }
  if (status === PLAYBOOK_INSIGHT_RUN_STATUS.failed) {
    return PLAYBOOK_SOURCE_STATE.failed;
  }
  return PLAYBOOK_SOURCE_STATE.idle;
};

// A source sits on the card once a run of it exists, whatever became of that run. Anyword data sits
// on every card: its run starts with the playbook, and a card without one still offers to start it.
export const isPlaybookSourceAdded = (playbook: Playbook, source: PlaybookInsightSource): boolean =>
  source === PLAYBOOK_INSIGHT_SOURCE.anyword || getPlaybookInsightRun(playbook, source) !== null;

const hasCompletedAnywordRun = (playbook: Playbook): boolean =>
  getPlaybookInsightRun(playbook, PLAYBOOK_INSIGHT_SOURCE.anyword)?.status ===
  PLAYBOOK_INSIGHT_RUN_STATUS.completed;

// Why the source cannot be started, or null when it can. The backend refuses both cases, so the
// card disables the action instead of firing a request that comes back as an error. Integration
// data and uploaded A/B tests read real ads against the insights the Anyword run already
// established, so they stay locked until that run has been completed and its insights saved.
export const getPlaybookSourceBlockedReason = (
  playbook: Playbook,
  source: PlaybookInsightSource
): PlaybookSourceBlockedReason | null => {
  const run = getPlaybookInsightRun(playbook, source);
  if (run && isPlaybookInsightRunPending(run.status)) {
    return null;
  }

  if (getActivePlaybookInsightRun(playbook)) {
    return PLAYBOOK_SOURCE_BLOCKED_REASON.runInProgress;
  }

  if (isIntegrationDataSource(source) && !hasCompletedAnywordRun(playbook)) {
    return PLAYBOOK_SOURCE_BLOCKED_REASON.anywordRequired;
  }

  return null;
};

// An integration run reports what it covered even when it found too little to draw an insight from,
// so a run below the floor still has coverage numbers to show.
export const isPlaybookInsightRunBelowFloor = (run?: PlaybookInsightRun | null): boolean =>
  run?.floorMet === false;
